import { randomBytes, utf8ToBytes, bytesToUtf8 } from './bytes.js';
import { decryptFromEnvelope, encryptToEnvelope, buildCanonicalAad } from './envelope.js';
import { derivePassphraseKey, KDF_ARGON2ID, KDF_KEY_LENGTH_BYTES } from './kdf.js';

export const KEY_BUNDLE_VERSION = 'amdmd-k1';
export const KEY_VERIFIER_PLAINTEXT = 'amandamd:key-verifier:v1';

function assertClientId(clientId) {
  if (typeof clientId !== 'string' || !clientId.trim()) {
    throw new Error('clientId is required.');
  }
}

function assertPassphrase(passphrase) {
  if (typeof passphrase !== 'string' || passphrase.length < 12) {
    throw new Error('Passphrase must be at least 12 characters.');
  }
}

function createKdfRequest(kdfName) {
  if ((kdfName || '').toLowerCase() === KDF_ARGON2ID) {
    return {
      name: 'argon2id',
      iterations: 3,
      memory_kib: 65536,
      parallelism: 1,
      pbkdf2_iterations: 600000,
      salt: randomBytes(16),
    };
  }

  return {
    name: 'pbkdf2',
    iterations: 600000,
    salt: randomBytes(16),
  };
}

export async function createClientKeyBundle({ clientId, passphrase, kdfName = KDF_ARGON2ID }) {
  assertClientId(clientId);
  assertPassphrase(passphrase);

  const kdfRequest = createKdfRequest(kdfName);
  const { keyBytes: kekBytes, kdfUsed } = await derivePassphraseKey({
    passphrase,
    kdf: kdfRequest,
    keyLengthBytes: KDF_KEY_LENGTH_BYTES,
    allowFallback: true,
  });

  const dekBytes = randomBytes(32);
  const wrappedDekAad = buildCanonicalAad({
    clientId,
    objectType: 'config',
    objectId: 'wrapped_dek',
  });
  const keyVerifierAad = buildCanonicalAad({
    clientId,
    objectType: 'config',
    objectId: 'key_verifier',
  });

  const wrapped_dek = await encryptToEnvelope({
    keyBytes: kekBytes,
    plaintextBytes: dekBytes,
    aadBytes: wrappedDekAad,
  });
  const key_verifier = await encryptToEnvelope({
    keyBytes: kekBytes,
    plaintextBytes: utf8ToBytes(KEY_VERIFIER_PLAINTEXT),
    aadBytes: keyVerifierAad,
  });

  return {
    keyBundle: {
      version: KEY_BUNDLE_VERSION,
      kdf: kdfUsed,
      wrapped_dek,
      key_verifier,
      created_at: new Date().toISOString(),
    },
    dekBytes,
  };
}

export async function unlockClientDek({ clientId, passphrase, keyBundle }) {
  assertClientId(clientId);
  assertPassphrase(passphrase);

  if (!keyBundle || keyBundle.version !== KEY_BUNDLE_VERSION) {
    throw new Error('Client key bundle is missing or invalid.');
  }

  try {
    const { keyBytes: kekBytes } = await derivePassphraseKey({
      passphrase,
      kdf: keyBundle.kdf,
      keyLengthBytes: KDF_KEY_LENGTH_BYTES,
      allowFallback: true,
    });

    const verifierBytes = await decryptFromEnvelope({
      keyBytes: kekBytes,
      envelope: keyBundle.key_verifier,
    });
    if (bytesToUtf8(verifierBytes) !== KEY_VERIFIER_PLAINTEXT) {
      throw new Error('Invalid verifier value.');
    }

    const dekBytes = await decryptFromEnvelope({
      keyBytes: kekBytes,
      envelope: keyBundle.wrapped_dek,
    });
    if (dekBytes.length !== 32) {
      throw new Error('Invalid DEK length.');
    }
    return dekBytes;
  } catch {
    throw new Error('Invalid passphrase or key bundle.');
  }
}
